import React, { useEffect } from "react";
import {
  Shield, Globe, Cpu, Target, Zap, FileText, Bot,
  ArrowRight, Check, ShieldCheck, Code2,
  PlayCircle, ChevronRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const ScanFort = () => {
  const navigate = useNavigate();
  const controls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    controls.start('visible');
  }, [controls]);

  return (
    <div className="text-[#2c2c2c] overflow-x-hidden">

      {/* Hero Section */}
      <section className="relative pt-12 md:pt-16 pb-20 md:pb-24 px-6 sm:px-8 lg:px-12 overflow-hidden bg-white/40 border-b border-[#e5eef6]">
        <div className="relative z-10 max-w-6xl mx-auto">
          <motion.div
            className="flex flex-col lg:flex-row items-center justify-between gap-16"
            initial="hidden"
            animate={controls}
            variants={staggerContainer}
          >
            <motion.div className="lg:w-1/2 space-y-6" variants={fadeInUp}>
              <motion.div
                className="inline-flex items-center space-x-2 bg-[#005a9c]/10 px-4 py-1.5 rounded-full border border-[#005a9c]/20"
                variants={fadeInUp}
              >
                <Shield className="w-4 h-4 text-[#005a9c]" />
                <span className="text-[#005a9c] text-sm font-medium tracking-wider uppercase">ScanFort</span>
              </motion.div>

              <motion.h1
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight text-slate-900"
                variants={fadeInUp}
              >
                Advanced AI <span className="text-[#005a9c]">Penetration Testing</span>
              </motion.h1>

              <motion.p
                className="text-lg text-slate-600 leading-relaxed max-w-xl"
                variants={fadeInUp}
              >
                Harness the power of artificial intelligence to proactively identify and mitigate security
                vulnerabilities before they can be exploited. Our platform automates threat
                detection and delivers actionable intelligence.
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row gap-4 pt-2"
                variants={fadeInUp}
              >
                <motion.button
                  onClick={() => navigate('/contactus')}
                  className="group relative bg-[#005a9c] text-white font-bold px-8 py-3.5 rounded-lg overflow-hidden transition-all duration-300 hover:bg-[#003d6b] flex items-center justify-center shadow-lg"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="relative z-10 flex items-center">
                    <span>REQUEST DEMO</span>
                    <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>

                <motion.button
                  onClick={() => navigate('/aboutus')}
                  className="flex items-center justify-center px-6 py-3.5 text-[#005a9c] font-medium rounded-lg border-2 border-[#005a9c] hover:bg-[#005a9c]/5 transition-colors"
                  whileHover={{ y: -2 }}
                >
                  <span className="flex items-center">
                    <PlayCircle className="w-5 h-5 mr-2" />
                    <span>WATCH DEMO</span>
                  </span>
                </motion.button>
              </motion.div>

              {/* Trust Badges */}
              <motion.div
                className="mt-8 flex flex-wrap items-center justify-center sm:justify-start gap-6 opacity-80"
                variants={fadeInUp}
              >
                <div className="flex items-center space-x-2">
                  <ShieldCheck className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-slate-600">Enterprise-Grade Security</span>
                </div>
                <div className="h-6 w-px bg-slate-300"></div>
                <div className="flex items-center space-x-2">
                  <Zap className="w-5 h-5 text-yellow-600" />
                  <span className="text-sm text-slate-600">AI-Powered Scanning</span>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="lg:w-1/2 relative"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative z-10 bg-white/80 backdrop-blur-lg rounded-2xl p-8 border border-[#e5eef6] shadow-2xl">
                <div className="relative">
                  <h3 className="text-xl font-bold mb-6 text-slate-900 flex items-center">
                    <Shield className="w-6 h-6 mr-2 text-[#005a9c]" />
                    Security Dashboard
                  </h3>
                  <div className="space-y-6">
                    {[
                      { name: 'AI Model Security', value: '98%', color: 'bg-green-500' },
                      { name: 'API Endpoints', value: '95%', color: 'bg-[#005a9c]' },
                      { name: 'Data Protection', value: '99%', color: 'bg-purple-500' },
                      { name: 'Network Security', value: '97%', color: 'bg-yellow-500' },
                    ].map((item, index) => (
                      <div key={index}>
                        <div className="flex justify-between text-sm text-slate-600 mb-1.5">
                          <span>{item.name}</span>
                          <span className="font-semibold">{item.value}</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full ${item.color} rounded-full`}
                            initial={{ width: 0 }}
                            animate={{ width: item.value }}
                            transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                          ></motion.div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 pt-6 border-t border-slate-100">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-[#005a9c]">24/7</div>
                        <div className="text-xs text-slate-500">Monitoring</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-[#005a9c]">99.9%</div>
                        <div className="text-xs text-slate-500">Accuracy</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-[#005a9c]">50+</div>
                        <div className="text-xs text-slate-500">Checks</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-[#f0f7ff] border-b border-[#e5eef6]">
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <div className="inline-flex items-center space-x-2 bg-yellow-400/10 px-4 py-1.5 rounded-full border border-yellow-400/20 mb-6">
                <Zap className="w-4 h-4 text-yellow-600" />
                <span className="text-yellow-600 text-sm font-medium tracking-wider uppercase">Security Insights</span>
              </div>
              <h2 className="text-4xl font-bold mb-6 text-slate-900">
                The State of AI Security <span className="text-[#005a9c]">2024</span>
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
                Understanding the evolving landscape of AI security threats and how organizations are responding.
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            {[
              {
                icon: <Shield className="w-6 h-6 text-[#005a9c]" />,
                title: "AI-Powered Attacks",
                text: "93% of businesses expect to face daily AI attacks over the next year",
                stat: "93%"
              },
              {
                icon: <Globe className="w-6 h-6 text-[#005a9c]" />,
                title: "Deepfake Threats",
                text: "Deepfake attacks projected to increase 50%-60% in 2024",
                stat: "60%"
              },
              {
                icon: <Cpu className="w-6 h-6 text-[#005a9c]" />,
                title: "Preparedness Gap",
                text: "60% of IT professionals feel unprepared for AI-generated threats",
                stat: "60%"
              },
              {
                icon: <Target className="w-6 h-6 text-[#005a9c]" />,
                title: "Shift to Prevention",
                text: "73% of teams prioritize AI-powered preventive strategies",
                stat: "73%"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5 }}
                className="rf-card p-6 bg-white"
              >
                <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center mb-6 border border-slate-100">
                  {item.icon}
                </div>
                <div className="text-3xl font-bold text-[#005a9c] mb-2">{item.stat}</div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2 leading-snug">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* AppSec Section */}
      <section className="relative py-24 bg-transparent border-b border-[#e5eef6]">
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-medium bg-[#005a9c]/10 text-[#005a9c] mb-4 uppercase tracking-widest">
              <Zap className="w-3.5 h-3.5 mr-1.5" />
              Enterprise Security
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Your AppSec Superpower for <span className="text-[#005a9c]">AI Security</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Advanced protection against the most sophisticated AI threats with our automated red teaming and security assessment platform.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: <FileText className="w-6 h-6 text-[#005a9c]" />,
                title: "Automated Red Teaming",
                description: "Run detailed, automated LLM attacks based on categorized threat profiles like jailbreaks and prompt injection attacks.",
                features: [
                  "Automated attack simulation",
                  "Comprehensive threat coverage",
                  "Real-time vulnerability detection"
                ]
              },
              {
                icon: <Bot className="w-6 h-6 text-[#005a9c]" />,
                title: "Human Augmented",
                description: "Develop targeted attack objectives tailored to your LLMs with business-specific goals, ensuring relevant insights.",
                features: [
                  "Expert security analysis",
                  "Custom attack scenarios",
                  "Actionable insights"
                ]
              },
              {
                icon: <Code2 className="w-6 h-6 text-[#005a9c]" />,
                title: "Customized Attack Libraries",
                description: "Includes 20,000+ known vulnerabilities targeting GenAI systems. Constantly updated with the latest research.",
                features: [
                  "Extensive vulnerability database",
                  "Regular threat updates",
                  "Custom rule creation"
                ]
              },
              {
                icon: <Zap className="w-6 h-6 text-[#005a9c]" />,
                title: "No-Code Integration",
                description: "Start scanning your custom endpoints in under 5 minutes using any base model. Automated scans run asynchronously.",
                features: [
                  "Quick setup",
                  "Model agnostic",
                  "API-first approach"
                ]
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="rf-card p-8 hover:border-[#005a9c]/30"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center mb-6 border border-slate-100 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 mb-6 text-sm leading-relaxed">{feature.description}</p>
                <ul className="space-y-3">
                  {feature.features.map((item, i) => (
                    <li key={i} className="flex items-center text-sm text-slate-500">
                      <Check className="w-4 h-4 text-green-600 mr-2 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Testing Section */}
      <section className="relative py-24 border-b border-[#e5eef6] overflow-hidden bg-[#f0f7ff]">
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-medium bg-[#005a9c]/10 text-[#005a9c] mb-4 uppercase tracking-widest">
                <Shield className="w-3.5 h-3.5 mr-1.5" />
                Comprehensive Security
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                End-to-End Security for <span className="text-[#005a9c]">AI Systems</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Our comprehensive security testing ensures your LLM applications and APIs are protected against the latest threats and vulnerabilities in the AI landscape.
              </p>

              <ul className="space-y-4">
                {[
                  "Comprehensive application and network security testing",
                  "Protection against LLM exploits including prompt injections",
                  "Deep insights into generative AI-specific risks",
                  "Collaboration with expert pen-testers specializing in AI"
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.1 * index }}
                  >
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-6 h-6 rounded-full bg-green-50 flex items-center justify-center border border-green-100">
                        <Check className="w-3.5 h-3.5 text-green-600" />
                      </div>
                    </div>
                    <span className="ml-3 text-slate-700 font-medium">{item}</span>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-10">
                <button 
                  onClick={() => navigate('/contactus')} 
                  className="px-8 py-4 bg-[#005a9c] text-white font-bold rounded-lg hover:bg-[#003d6b] transition-all duration-300 flex items-center shadow-lg shadow-[#005a9c]/20"
                >
                  Get Started or Schedule a Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative">
                <div className="relative bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-800">
                  <div className="p-4 bg-slate-800/50 border-b border-slate-700">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
                      <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
                      <div className="w-3 h-3 rounded-full bg-[#27c93f]"></div>
                      <div className="flex-1 text-center text-xs font-mono text-slate-400 uppercase tracking-tighter">security-scan.log</div>
                    </div>
                  </div>
                  <div className="p-8 font-mono text-sm leading-relaxed">
                    <div className="space-y-4">
                      <div className="flex">
                        <span className="text-purple-400">const</span>
                        <span className="text-yellow-400 ml-2">scanResults</span>
                        <span className="text-slate-300"> = await </span>
                        <span className="text-blue-400">scanAISystem</span>
                        <span className="text-slate-300">(</span>
                        <span className="text-green-400">'rakfort-endpoint'</span>
                        <span className="text-slate-300">);</span>
                      </div>

                      <div className="text-slate-500 italic">{'// Initializing neural threat analyzer...'}</div>

                      <div className="space-y-2 pl-4 border-l-2 border-[#005a9c]/30">
                        <div className="text-green-400 flex items-center gap-2">
                          <Check className="w-3 h-3" />
                          Scanned 1,248 API endpoints
                        </div>
                        <div className="text-green-400 flex items-center gap-2">
                          <Check className="w-3 h-3" />
                          Detected 12 potential vulnerabilities
                        </div>
                        <div className="text-yellow-400 flex items-center gap-2">
                          <span className="text-xs">!</span>
                          3 critical issues require attention
                        </div>
                        <div className="text-[#005a9c] flex items-center gap-2">
                          <span className="text-xs">i</span>
                          9 medium severity issues found
                        </div>
                      </div>

                      <div className="pt-4">
                        <div className="flex items-center text-yellow-400 gap-3">
                          <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                          <span>Generating comprehensive security report...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-[#005a9c] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-3xl md:text-4xl font-bold mb-6">Ready to Secure Your AI Systems?</h3>
            <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
              Join leading enterprises that trust RakFort to protect their AI infrastructure from emerging threats. 
              Start your journey towards a more secure AI ecosystem today.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button 
                onClick={() => navigate('/contactus')} 
                className="px-8 py-4 bg-white text-[#005a9c] font-bold rounded-lg hover:bg-slate-50 transition-all duration-300 flex items-center justify-center shadow-xl"
              >
                Schedule a Demo
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ScanFort;
